import React, { useState, useEffect } from 'react';
import {
  fetchVideoLeaderboard,
  fetchLikesReceivedLeaderboard,
  fetchEngagementLeaderboard,
  fetchInactiveUsers,
  fetchRegistrationTrends,
  type LeaderboardEntry,
  type InactiveUser,
  type TrendPoint,
} from '../../lib/analyticsService';
import { logger } from '../../lib/logger';

type Tab = 'videos' | 'likes' | 'engagement' | 'inactive' | 'trends' | 'funnel';

// Onglets masqués (simplification UI 14/07) : 'engagement' (redondant avec la
// page Engagement, déplacée sous Piloter), 'trends' et 'funnel'. Le code de
// rendu de ces onglets est conservé — il suffit de les ré-ajouter ici pour
// les réactiver.
const tabs: { id: Tab; label: string; icon: string }[] = [
  { id: 'videos', label: 'Top Vidéos', icon: '🎬' },
  { id: 'likes', label: 'Likes reçus', icon: '❤️' },
  { id: 'inactive', label: 'Inactifs', icon: '💤' },
];

const periodOptions = [
  { value: 7, label: '7 jours' },
  { value: 30, label: '30 jours' },
  { value: 90, label: '90 jours' },
  { value: 0, label: 'Tout' },
];

const inactivityOptions = [
  { value: 7, label: '7 jours' },
  { value: 14, label: '14 jours' },
  { value: 30, label: '30 jours' },
  { value: 60, label: '60 jours' },
];

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-yellow-100 text-yellow-700 text-xs font-bold">1</span>;
  if (rank === 2) return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-700 text-xs font-bold">2</span>;
  if (rank === 3) return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">3</span>;
  return <span className="inline-flex items-center justify-center w-7 h-7 text-gray-400 text-xs font-medium">{rank}</span>;
}

function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2 w-32">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 w-8 text-right">{value}</span>
    </div>
  );
}

function SeedBadge({ isSeed }: { isSeed: boolean }) {
  if (!isSeed) return null;
  return <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-indigo-100 text-indigo-700 uppercase ml-1">seed</span>;
}

const AnalyticsPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('videos');
  const [period, setPeriod] = useState(30);
  const [excludeSeeds, setExcludeSeeds] = useState(false);
  const [inactiveThreshold, setInactiveThreshold] = useState(14);
  const [inactiveType, setInactiveType] = useState<'all' | 'candidate' | 'employer'>('all');
  const [loading, setLoading] = useState(false);

  const [videoBoard, setVideoBoard] = useState<LeaderboardEntry[]>([]);
  const [likesBoard, setLikesBoard] = useState<LeaderboardEntry[]>([]);
  const [engagementBoard, setEngagementBoard] = useState<(LeaderboardEntry & { likes: number; views: number; swipes: number; favorites: number })[]>([]);
  const [inactiveList, setInactiveList] = useState<InactiveUser[]>([]);
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [funnelData, setFunnelData] = useState<{
    seedContentViewed: number;
    realActionsTriggered: number;
    conversionRate: number;
    byCampaign: { campaign: string; viewed: number; actions: number; rate: number }[];
  } | null>(null);

  const loadTab = async () => {
    setLoading(true);
    try {
      if (tab === 'videos') {
        setVideoBoard(await fetchVideoLeaderboard({ period, excludeSeeds }));
      } else if (tab === 'likes') {
        setLikesBoard(await fetchLikesReceivedLeaderboard({ period, excludeSeeds }));
      } else if (tab === 'engagement') {
        setEngagementBoard(await fetchEngagementLeaderboard({ period, excludeSeeds }));
      } else if (tab === 'inactive') {
        setInactiveList(await fetchInactiveUsers({ thresholdDays: inactiveThreshold, userType: inactiveType, excludeSeeds }));
      } else if (tab === 'trends') {
        setTrends(await fetchRegistrationTrends(period || 365));
      } else if (tab === 'funnel') {
        const { supabase } = await import('../../lib/supabase');
        // Seed content viewed by real users
        const { data: seedViews } = await supabase
          .from('video_interactions')
          .select('user_id, video_id, seed_tag')
          .is('seed_tag', null);

        const { data: seedVideos } = await supabase
          .from('candidate_videos')
          .select('id')
          .not('seed_tag', 'is', null);

        const seedVideoIds = new Set((seedVideos || []).map((v: any) => v.id));

        const viewsOnSeed = (seedViews || []).filter((v: any) => seedVideoIds.has(v.video_id));

        const { data: seedOffers } = await supabase
          .from('job_offers')
          .select('id, seed_tag')
          .not('seed_tag', 'is', null);

        const seedOfferIds = new Set((seedOffers || []).map((o: any) => o.id));

        const { data: realApps } = await supabase
          .from('applications')
          .select('job_offer_id')
          .is('seed_tag', null);

        const realAppsOnSeed = (realApps || []).filter((a: any) => seedOfferIds.has(a.job_offer_id));

        const viewed = viewsOnSeed.length;
        const actions = realAppsOnSeed.length;
        const rate = viewed > 0 ? Math.round((actions / viewed) * 10000) / 100 : 0;

        const campaignMap: Record<string, { viewed: number; actions: number }> = {};
        (seedOffers || []).forEach((o: any) => {
          if (!campaignMap[o.seed_tag]) campaignMap[o.seed_tag] = { viewed: 0, actions: 0 };
        });

        realAppsOnSeed.forEach((a: any) => {
          const offer = (seedOffers || []).find((o: any) => o.id === a.job_offer_id);
          if (offer?.seed_tag && campaignMap[offer.seed_tag]) {
            campaignMap[offer.seed_tag].actions++;
          }
        });

        setFunnelData({
          seedContentViewed: viewed,
          realActionsTriggered: actions,
          conversionRate: rate,
          byCampaign: Object.entries(campaignMap).map(([campaign, data]) => ({
            campaign,
            viewed: data.viewed,
            actions: data.actions,
            rate: data.viewed > 0 ? Math.round((data.actions / data.viewed) * 10000) / 100 : 0,
          })),
        });
      }
    } catch (err) {
      logger.error('load_analytics_tab', 'AnalyticsPage', { error: String(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTab(); }, [tab, period, excludeSeeds, inactiveThreshold, inactiveType]);

  const maxVideo = videoBoard[0]?.value || 1;
  const maxLikes = likesBoard[0]?.value || 1;
  const maxEng = engagementBoard[0]?.value || 1;

  const renderFilters = () => {
    if (tab === 'inactive') {
      return (
        <div className="flex items-center gap-3">
          <select value={inactiveThreshold} onChange={(e) => setInactiveThreshold(Number(e.target.value))} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm">
            {inactivityOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={inactiveType} onChange={(e) => setInactiveType(e.target.value as any)} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm">
            <option value="all">Tous</option>
            <option value="candidate">Candidats</option>
            <option value="employer">Recruteurs</option>
          </select>
          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={excludeSeeds} onChange={(e) => setExcludeSeeds(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            Exclure seeds
          </label>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3">
        <select value={period} onChange={(e) => setPeriod(Number(e.target.value))} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm">
          {periodOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={excludeSeeds} onChange={(e) => setExcludeSeeds(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
          Exclure seeds
        </label>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
        </div>
      );
    }

    if (tab === 'videos') {
      if (videoBoard.length === 0) return <EmptyState text="Aucune vidéo dans cette période" />;
      return (
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b border-gray-100">
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 w-12">#</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Utilisateur</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Vidéos</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Dernière</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {videoBoard.map((e) => (
              <tr key={e.userId} className="hover:bg-gray-50/50">
                <td className="px-4 py-3"><RankBadge rank={e.rank} /></td>
                <td className="px-4 py-3 text-sm"><span className="font-medium text-gray-900">{e.name}</span><SeedBadge isSeed={e.isSeed} /></td>
                <td className="px-4 py-3"><ProgressBar value={e.value} max={maxVideo} /></td>
                <td className="px-4 py-3 text-xs text-gray-500">{e.lastActivity || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (tab === 'likes') {
      if (likesBoard.length === 0) return <EmptyState text="Aucun like dans cette période" />;
      return (
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b border-gray-100">
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 w-12">#</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Utilisateur</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Likes reçus</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {likesBoard.map((e) => (
              <tr key={e.userId} className="hover:bg-gray-50/50">
                <td className="px-4 py-3"><RankBadge rank={e.rank} /></td>
                <td className="px-4 py-3 text-sm"><span className="font-medium text-gray-900">{e.name}</span><SeedBadge isSeed={e.isSeed} /></td>
                <td className="px-4 py-3"><ProgressBar value={e.value} max={maxLikes} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (tab === 'engagement') {
      if (engagementBoard.length === 0) return <EmptyState text="Aucune activité dans cette période" />;
      return (
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b border-gray-100">
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 w-12">#</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Utilisateur</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Likes</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Vues</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Swipes</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Favoris</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Total</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {engagementBoard.map((e) => (
              <tr key={e.userId} className="hover:bg-gray-50/50">
                <td className="px-4 py-3"><RankBadge rank={e.rank} /></td>
                <td className="px-4 py-3 text-sm">
                  <span className="font-medium text-gray-900">{e.name}</span>
                  <SeedBadge isSeed={e.isSeed} />
                  <span className={`ml-2 px-1.5 py-0.5 text-[10px] font-medium rounded ${e.type === 'candidate' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>
                    {e.type === 'candidate' ? 'C' : 'R'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{e.likes}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{e.views}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{e.swipes}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{e.favorites}</td>
                <td className="px-4 py-3"><ProgressBar value={e.value} max={maxEng} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (tab === 'inactive') {
      if (inactiveList.length === 0) return <EmptyState text="Aucun utilisateur inactif avec ce seuil" />;
      return (
        <div>
          <p className="px-4 py-2 text-xs text-gray-500">{inactiveList.length} utilisateurs inactifs depuis {inactiveThreshold}+ jours</p>
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Nom</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Type</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Email</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Inscrit le</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Dernière activité</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Inactif</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-50">
              {inactiveList.slice(0, 50).map((u) => (
                <tr key={u.userId} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 text-sm">
                    <span className="font-medium text-gray-900">{u.name}</span>
                    <SeedBadge isSeed={u.isSeed} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${u.type === 'candidate' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                      {u.type === 'candidate' ? 'Candidat' : 'Recruteur'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{u.email}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{u.registeredAt}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{u.lastActivity || 'Jamais'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-md ${
                      u.inactiveDays >= 30 ? 'bg-red-50 text-red-600' : u.inactiveDays >= 14 ? 'bg-orange-50 text-orange-600' : 'bg-yellow-50 text-yellow-600'
                    }`}>
                      {u.inactiveDays}j
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (tab === 'trends') {
      if (trends.length === 0) return <EmptyState text="Aucune inscription dans cette période" />;
      const maxDay = Math.max(...trends.map((t) => t.candidates + t.employers + t.seedCandidates + t.seedEmployers), 1);
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4 px-4">
            <div className="text-center">
              <p className="text-lg font-bold text-blue-600">{trends.reduce((s, t) => s + t.candidates, 0)}</p>
              <p className="text-xs text-gray-500">Candidats réels</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-purple-600">{trends.reduce((s, t) => s + t.employers, 0)}</p>
              <p className="text-xs text-gray-500">Recruteurs réels</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-indigo-400">{trends.reduce((s, t) => s + t.seedCandidates, 0)}</p>
              <p className="text-xs text-gray-500">Candidats seeds</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-purple-400">{trends.reduce((s, t) => s + t.seedEmployers, 0)}</p>
              <p className="text-xs text-gray-500">Recruteurs seeds</p>
            </div>
          </div>

          <div className="px-4 space-y-1">
            {trends.map((t) => {
              const total = t.candidates + t.employers + t.seedCandidates + t.seedEmployers;
              return (
                <div key={t.date} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-20 text-right font-mono">{t.date.slice(5)}</span>
                  <div className="flex-1 flex h-5 rounded overflow-hidden bg-gray-50">
                    {t.candidates > 0 && (
                      <div className="bg-blue-500 h-full" style={{ width: `${(t.candidates / maxDay) * 100}%` }} title={`${t.candidates} candidats`} />
                    )}
                    {t.employers > 0 && (
                      <div className="bg-purple-500 h-full" style={{ width: `${(t.employers / maxDay) * 100}%` }} title={`${t.employers} recruteurs`} />
                    )}
                    {t.seedCandidates > 0 && (
                      <div className="bg-blue-300 h-full" style={{ width: `${(t.seedCandidates / maxDay) * 100}%` }} title={`${t.seedCandidates} seeds candidats`} />
                    )}
                    {t.seedEmployers > 0 && (
                      <div className="bg-purple-300 h-full" style={{ width: `${(t.seedEmployers / maxDay) * 100}%` }} title={`${t.seedEmployers} seeds recruteurs`} />
                    )}
                  </div>
                  <span className="text-xs text-gray-500 w-8 text-right">{total}</span>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-center gap-4 px-4 pt-2">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-blue-500" /><span className="text-xs text-gray-500">Candidats</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-purple-500" /><span className="text-xs text-gray-500">Recruteurs</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-blue-300" /><span className="text-xs text-gray-500">Seeds candidats</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-purple-300" /><span className="text-xs text-gray-500">Seeds recruteurs</span></div>
          </div>
        </div>
      );
    }

    if (tab === 'funnel') {
      if (!funnelData) return <EmptyState text="Chargement des données funnel..." />;
      return (
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-indigo-50 rounded-xl p-5 text-center">
              <p className="text-3xl font-bold text-indigo-600">{funnelData.seedContentViewed}</p>
              <p className="text-sm text-gray-600 mt-1">Contenu seed vu par des vrais users</p>
              <p className="text-xs text-gray-400 mt-0.5">Interactions réelles sur vidéos/offres seeds</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-5 text-center">
              <p className="text-3xl font-bold text-emerald-600">{funnelData.realActionsTriggered}</p>
              <p className="text-sm text-gray-600 mt-1">Actions réelles déclenchées</p>
              <p className="text-xs text-gray-400 mt-0.5">Candidatures réelles sur offres seeds</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-5 text-center">
              <p className="text-3xl font-bold text-amber-600">{funnelData.conversionRate}%</p>
              <p className="text-sm text-gray-600 mt-1">Taux de conversion</p>
              <p className="text-xs text-gray-400 mt-0.5">Vues seed → action organique</p>
            </div>
          </div>

          {funnelData.byCampaign.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Par campagne</h3>
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Campagne</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Actions réelles</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Taux</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {funnelData.byCampaign.map((c) => (
                    <tr key={c.campaign} className="hover:bg-gray-50/50">
                      <td className="px-4 py-2.5 text-sm font-mono text-gray-700">{c.campaign}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-600">{c.actions}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-md ${
                          c.rate > 5 ? 'bg-emerald-50 text-emerald-600' : c.rate > 0 ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-400'
                        }`}>
                          {c.rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
            <strong>ROI Seed :</strong> {funnelData.realActionsTriggered > 0
              ? `~${Math.ceil(funnelData.seedContentViewed / funnelData.realActionsTriggered)} vues seed nécessaires pour 1 action organique`
              : 'Pas encore assez de données pour calculer le ROI'}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      {/* Tab bar */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              tab === t.id
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span>{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{tabs.find((t) => t.id === tab)?.label}</h2>
        {renderFilters()}
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {renderContent()}
      </div>
    </div>
  );
};

function EmptyState({ text }: { text: string }) {
  return (
    <div className="py-16 text-center text-sm text-gray-400">
      {text}
    </div>
  );
}

export default AnalyticsPage;
